import { useAuthQuery } from '@/api/index.ts';
import { studyQueryKeys } from '@/config/queryKeys';
import { canUserEditStudy } from '@/config/studies';
import { StudyInfoSection } from '@/pages/Study/components/StudyInfoSection';
import { StudyTitleBar } from '@/pages/Study/components/StudyTitleBar';
import {
  TauSamplesDetailTable,
  type TauSamplesSummary,
  type TauSamplesSummarySource,
} from '@/pages/Study/components/TauSamplesDetailTable';
import { TauSamplesSummaryBar } from '@/pages/Study/components/TauSamplesSummaryBar';
import { fetchStudy } from '@/services/studies';
import { useAppStore } from '@/stores/app';
import { SampleProteoformPublic, Study } from '@/types';
import { FullLoading } from '@components/index';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ErrorComponent, createLazyFileRoute } from '@tanstack/react-router';
import type { AgGridReact } from 'ag-grid-react';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import studyStyles from '../../pages/Studies/styles.module.css';
import '../../styles/components/ag-grid-overrides.css';
import detailsPageStyles from '../../styles/components/details.page.module.css';
import pageStyles from '../../styles/pages/index.module.css';

interface SummaryState {
  source: TauSamplesSummarySource;
  summary: TauSamplesSummary | null;
  failed: boolean;
}

const sameSource = (a: TauSamplesSummarySource, b: TauSamplesSummarySource) =>
  a.orgId === b.orgId && a.studyId === b.studyId && a.assayId === b.assayId;

const sameSummary = (a: TauSamplesSummary | null, b: TauSamplesSummary) =>
  a !== null &&
  a.numSamples === b.numSamples &&
  a.numProteoformGroups === b.numProteoformGroups &&
  a.numPtms === b.numPtms &&
  a.numIsoforms === b.numIsoforms;

export const Route = createLazyFileRoute('/studies/$studyId_/tau-samples')({
  component: RouteComponent,
  errorComponent: e => <ErrorComponent {...e} />,
  pendingComponent: () => {
    const { t } = useTranslation('studies');
    return <FullLoading label={`${t('fetching')}...`} />;
  },
});

function RouteComponent() {
  const { t } = useTranslation('studies');
  // Mirror the loader's fallback: selectedOrganization is not persisted and is undefined
  // on the first render after a hard reload. selectedOrgStub IS persisted (localStorage,
  // synchronous), so it's available immediately. Without the fallback, selectedOrg is null
  // on render 1 (early return, fewer hooks) then non-null on render 2 — violating the
  // Rules of Hooks.
  const selectedOrg = useAppStore(useShallow(state => state.selectedOrganization ?? state.selectedOrgStub));
  const { studyId } = Route.useParams();
  const orgId = selectedOrg?.id ?? '';

  const { data, isLoading } = useSuspenseQuery<Study>({
    queryKey: studyQueryKeys.detail(orgId, studyId),
    queryFn: () => fetchStudy(orgId, studyId),
  });

  const { data: currentUser } = useAuthQuery();
  const canEdit = canUserEditStudy(data, currentUser ?? null);
  const tauAssay = data?.assays?.find(a => a.assay_type === 'tau_proteoform');
  const assayId = tauAssay?.assay_id ?? '';
  // Declared before the org early-return: every hook must run unconditionally (see CLAUDE.md
  // "Route Component React Hook Order").
  const [summaryState, setSummaryState] = React.useState<SummaryState | null>(null);
  // Owned here rather than inside the table because the summary bar's download button needs the
  // same grid's live sort/filter state, and the two are siblings.
  const tauGridRef = useRef<AgGridReact<SampleProteoformPublic>>(null);

  // The callbacks must stay referentially stable (they feed the table's datasource memo), so the
  // source they compare against has to reach them through a ref rather than a closure.
  const currentSource = React.useRef<TauSamplesSummarySource>({ orgId, studyId, assayId });
  React.useEffect(() => {
    currentSource.current = { orgId, studyId, assayId };
  }, [orgId, studyId, assayId]);

  // Refusing the write matters on its own: responses can resolve out of order, and without this a
  // straggler from the previous org/study/assay would evict counts that had already arrived for the
  // current one, leaving the strip spinning until something else triggered a refetch.
  const handleSummaryChange = React.useCallback((next: TauSamplesSummary, source: TauSamplesSummarySource) => {
    if (!sameSource(source, currentSource.current)) return;
    // Every block fetch re-reports the same four counts; returning `prev` lets React skip a
    // re-render of the whole route on each page of the grid.
    setSummaryState(prev =>
      prev && !prev.failed && sameSource(prev.source, source) && sameSummary(prev.summary, next)
        ? prev
        : { source, summary: next, failed: false },
    );
  }, []);
  const handleSummaryError = React.useCallback((source: TauSamplesSummarySource) => {
    if (sameSource(source, currentSource.current)) setSummaryState({ source, summary: null, failed: true });
  }, []);

  // Re-checked on render because the switch happens after the write: this is what clears the strip
  // as the new heading appears, instead of leaving the previous scope's numbers under it.
  const current = summaryState && sameSource(summaryState.source, { orgId, studyId, assayId }) ? summaryState : null;

  if (!selectedOrg?.id) {
    return <ErrorComponent error={new Error(t('study.no_org_selected'))} />;
  }

  const emptyTxt = t('empty', { ns: 'general' });

  return (
    <Stack as="section" className={studyStyles.container} fullWidth orientation="vertical">
      <StudyTitleBar
        canEdit={canEdit}
        data={data}
        detailTitle={t('study.tauDetailTitle')}
        orgId={orgId}
        pageStyles={pageStyles}
        studyStyles={studyStyles}
        t={t}
      />
      <Stack fullWidth gap={4} orientation="vertical">
        <Stack as="header" className={detailsPageStyles.header}>
          <StudyInfoSection data={data} emptyTxt={emptyTxt} isLoading={isLoading} t={t} />
        </Stack>

        <Stack alignItems="center" fullWidth justifyContent="space-between" orientation="horizontal">
          <Text as="h3">{t('study.tauDetailTitle')}</Text>
          {/* The counts ride on the table's own search envelope, so there is nothing to show
              until the assay — and therefore the table — is known. */}
          {tauAssay ? (
            <TauSamplesSummaryBar
              assayId={tauAssay.assay_id}
              gridRef={tauGridRef}
              hasError={current?.failed ?? false}
              orgId={orgId}
              studyId={studyId}
              summary={current?.summary ?? null}
            />
          ) : null}
        </Stack>
        {/* `assay_id` is a required field on the search request, so there is nothing to query
            until the study's Tau assay is known. */}
        {tauAssay ? (
          <TauSamplesDetailTable
            assayId={tauAssay.assay_id}
            gridRef={tauGridRef}
            onSummaryChange={handleSummaryChange}
            onSummaryError={handleSummaryError}
            orgId={orgId}
            studyId={studyId}
          />
        ) : (
          <Text>{emptyTxt}</Text>
        )}
      </Stack>
    </Stack>
  );
}
